import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { savedFiltersService } from './service';
import type {
  CreateSavedFilterInput,
  SavedFilterScope,
  UpdateSavedFilterInput,
} from './types';

const KEYS = {
  all: ['saved-filters'] as const,
  byScope: (scope: SavedFilterScope) =>
    [...KEYS.all, 'scope', scope] as const,
};

export function useSavedFilters(scope: SavedFilterScope) {
  return useQuery({
    queryKey: KEYS.byScope(scope),
    queryFn: () => savedFiltersService.list(scope),
    // Filters change rarely — keep them warm so the dropdown is
    // instant on subsequent visits.
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateSavedFilter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSavedFilterInput) =>
      savedFiltersService.create(input),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: KEYS.byScope(vars.scope) });
    },
  });
}

export function useUpdateSavedFilter(scope: SavedFilterScope) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; patch: UpdateSavedFilterInput }) =>
      savedFiltersService.update(input.id, input.patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.byScope(scope) });
    },
  });
}

export function useDeleteSavedFilter(scope: SavedFilterScope) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => savedFiltersService.delete(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS.byScope(scope) });
    },
  });
}
